/*
  (c) 2025 Fluxonix Inc.
*/

// src/pages/BillingPage.jsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Stack
} from "@mui/material";
import { useAuth } from "../auth/AuthProvider";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import RequireRole from "../components/RequireRole";

export default function BillingPage() {
  const { token, hasRole } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [status, setStatus] = useState(null);
  const [annualPriceCents, setAnnualPriceCents] = useState(1000000);
  const [entitlements, setEntitlements] = useState(null);
  const [billingManagement, setBillingManagement] = useState(null);
  const [error, setError] = useState(null);

  const fetchSubscriptionInfo = async () => {
  try {
    const res = await api.get("/api/stripe/user-subscription");
    setPlan(res.data.plan || "Unknown");
    setStatus(res.data.status || "Unknown");
    setAnnualPriceCents(res.data.annual_workflow_price_cents || 1000000);
    setEntitlements(res.data.entitlements || null);
    setBillingManagement(res.data.billing_management || "direct");
  } catch (err) {
    setPlan("Not Subscribed");
    setStatus("Inactive");
  } finally {
    setLoading(false);
  }
};

  const startCheckout = async (payload) => {
    try {
      setError(null);
      const res = await api.post("/api/stripe/create-checkout-session", payload);
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail?.message || err.response?.data?.detail || "Failed to start checkout.");
    }
  };

  const handleUpgrade = () => startCheckout({
    purchase_type: "workflow_license",
    workflow_key: "quality_incident_ncr",
  });

  const handleCapacityPack = (workflowLicenseId) => startCheckout({
    purchase_type: "capacity_pack",
    workflow_license_id: workflowLicenseId,
    quantity: 1,
  });

  const handleManageBilling = async () => {
    try {
      const res = await api.post("/api/stripe/customer-portal");
      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || "Billing management could not be opened.");
    }
  };

  useEffect(() => {
    if (!hasRole("admin")) {
      navigate("/unauthorized");
      return;
    }

    fetchSubscriptionInfo();
  }, [hasRole, navigate]);

  return (
    <RequireRole role="admin">
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Billing & Subscription
        </Typography>

        {loading ? (
          <CircularProgress />
        ) : (
          <Card sx={{ maxWidth: 600 }}>
            <CardContent>
              {error && <Alert severity="error">{error}</Alert>}

              <Typography variant="body1">
                <strong>Plan:</strong> {plan}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Status:</strong> {status}
              </Typography>

              <Typography variant="body2" sx={{ mt: 2 }}>
                <a href="https://fluxonix.io/#offer">Understand trial &amp; plans</a>
                {" — view the public guide to trial access and production scope."}
              </Typography>

              {status === "active" && billingManagement === "stripe" ? (
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={handleManageBilling}
                  >
                    Manage Billing
                  </Button>
                </Box>
              ) : status === "active" && billingManagement === "internal" ? (
                <Alert severity="info" sx={{ mt: 3 }}>
                  This is an internal Fluxonix workspace. Customer billing actions do not apply.
                </Alert>
              ) : status === "active" ? (
                <Alert severity="info" sx={{ mt: 3 }}>
                  This subscription is managed directly by Fluxonix. Contact Fluxonix to make billing changes.
                </Alert>
              ) : (
                <Button variant="contained" onClick={handleUpgrade} sx={{ mt: 3 }}>
                  Upgrade annually — ${(annualPriceCents / 100).toLocaleString()}
                </Button>
              )}

              {entitlements?.workflows?.length > 0 && (
                <Stack spacing={2} sx={{ mt: 3 }}>
                  <Divider />
                  {entitlements.workflows.map((item) => (
                    <Box key={item.license_id}>
                      <Typography variant="subtitle1">{item.workflow.label}</Typography>
                      <Typography variant="body2">
                        {item.investigations.used} of {item.investigations.limit} investigations used
                      </Typography>
                      {item.notification_level === "warning" && (
                        <Alert severity="info" sx={{ mt: 1 }}>5 investigations or fewer remain.</Alert>
                      )}
                      {item.notification_level === "critical" && (
                        <Alert severity="warning" sx={{ mt: 1 }}>2 investigations or fewer remain.</Alert>
                      )}
                      {item.notification_level === "limit_reached" && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          Annual capacity reached. Your 30-day grace begins with the next investigation.
                        </Alert>
                      )}
                      {item.grace.active && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          Grace period active through {new Date(item.grace.ends_at).toLocaleDateString()}.
                        </Alert>
                      )}
                      {item.processing_on_hold && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          New AI processing is on hold. Capture remains available; add capacity to resume processing.
                        </Alert>
                      )}
                      {billingManagement === "stripe" && (
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mt: 1 }}
                          onClick={() => handleCapacityPack(item.license_id)}
                        >
                          Add 25 investigations — ${((annualPriceCents * 0.5) / 100).toLocaleString()}
                        </Button>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </RequireRole>
  );
}
